import { useEffect, useState } from 'react';
import { Badge, Card, ErrorNote, Input, Spinner, api, brl, cx } from '@mobi/web';
import { PaymentRides } from './PaymentRides.js';

interface EarningsResponse {
  from: string;
  to: string;
  rides: number;
  cents: number;
  walletCents: number;
  byMethod: Record<string, { rides: number; cents: number }>;
}

const METHOD_LABEL: Record<string, string> = {
  cash: 'Dinheiro',
  pix: 'Pix',
  credit: 'Crédito',
  debit: 'Débito',
  unknown: 'Não informado',
};

/** yyyy-mm-dd no fuso local — toISOString() usaria UTC e viraria o dia perto da meia-noite. */
function isoDay(d: Date): string {
  const tz = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

const TODAY = () => isoDay(new Date());
const DAYS_AGO = (n: number) => isoDay(new Date(Date.now() - n * 86_400_000));

type PeriodKey = 'hoje' | 'ontem' | 'semana' | 'mes' | 'custom';

const PERIODS: { key: PeriodKey; label: string; range?: () => [string, string] }[] = [
  { key: 'hoje', label: 'Hoje', range: () => [TODAY(), TODAY()] },
  { key: 'ontem', label: 'Ontem', range: () => [DAYS_AGO(1), DAYS_AGO(1)] },
  { key: 'semana', label: '7 dias', range: () => [DAYS_AGO(6), TODAY()] },
  { key: 'mes', label: '30 dias', range: () => [DAYS_AGO(29), TODAY()] },
  { key: 'custom', label: 'Período' },
];

/**
 * Some no banco, não no celular: `/api/driver/earnings` já devolve o
 * total e a quebra por modalidade prontos. Um mês de corridas são
 * centenas de linhas — o motorista não precisa baixar isso para ver
 * quatro números.
 */
export function EarningsSummary() {
  const [period, setPeriod] = useState<PeriodKey>('hoje');
  const [customFrom, setCustomFrom] = useState(TODAY());
  const [customTo, setCustomTo] = useState(TODAY());
  const [data, setData] = useState<EarningsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [drilldown, setDrilldown] = useState<string | null>(null);

  const [from, to] =
    period === 'custom'
      ? [customFrom, customTo]
      : (PERIODS.find((p) => p.key === period)?.range?.() ?? [TODAY(), TODAY()]);

  useEffect(() => {
    if (from > to) return;
    setLoading(true);
    setError(null);
    api<EarningsResponse>(`/api/driver/earnings?from=${from}&to=${to}`)
      .then(setData)
      .catch(() => setError('não foi possível carregar o período'))
      .finally(() => setLoading(false));
  }, [from, to]);

  const label = period === 'hoje' ? 'Hoje' : PERIODS.find((p) => p.key === period)?.label ?? '';

  return (
    <Card tone="brand">
      <div className="flex flex-wrap gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPeriod(p.key)}
            className={cx(
              'rounded-full px-3 py-1.5 text-xs font-semibold transition',
              period === p.key ? 'bg-brand-ink/20' : 'bg-brand-ink/5 opacity-70',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {period === 'custom' && (
        <div className="mt-3 flex items-center gap-2">
          <Input
            type="date"
            value={customFrom}
            max={customTo}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="bg-brand-ink/10 text-brand-ink"
          />
          <span className="opacity-70">até</span>
          <Input
            type="date"
            value={customTo}
            min={customFrom}
            max={TODAY()}
            onChange={(e) => setCustomTo(e.target.value)}
            className="bg-brand-ink/10 text-brand-ink"
          />
        </div>
      )}

      {error && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}

      {loading ? (
        <div className="mt-4">
          <Spinner className="h-6 w-6" />
        </div>
      ) : (
        data && (
          <>
            <p className="eyebrow mt-4 opacity-70">{label}</p>
            <p className="display mt-1 text-5xl leading-none">{brl(data.cents)}</p>
            <p className="mt-3 text-sm opacity-80">
              {data.rides === 0
                ? 'nenhuma corrida concluída'
                : `${data.rides} corrida${data.rides > 1 ? 's' : ''} concluída${data.rides > 1 ? 's' : ''}`}
            </p>

            {data.rides > 0 && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-3 text-sm font-semibold underline decoration-dotted opacity-90"
              >
                {expanded ? 'ocultar por modalidade' : 'ver por modalidade'}
              </button>
            )}

            {expanded && (
              <div className="mt-3 space-y-2">
                {Object.entries(data.byMethod).map(([method, bucket]) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => method !== 'unknown' && setDrilldown(method)}
                    disabled={method === 'unknown'}
                    className="flex w-full items-center justify-between rounded-xl bg-brand-ink/10 px-3 py-2 text-left disabled:cursor-default"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <Badge tone="neutral">{bucket.rides}</Badge>
                      {METHOD_LABEL[method] ?? method}
                    </span>
                    <span className="tabular font-semibold">{brl(bucket.cents)}</span>
                  </button>
                ))}
                {data.walletCents > 0 && (
                  <p className="text-xs opacity-70">
                    + {brl(data.walletCents)} quitados direto pela carteira do passageiro, sem
                    passar pela sua mão
                  </p>
                )}
              </div>
            )}
          </>
        )
      )}

      {drilldown && (
        <PaymentRides
          method={drilldown}
          methodLabel={METHOD_LABEL[drilldown] ?? drilldown}
          from={from}
          to={to}
          onClose={() => setDrilldown(null)}
        />
      )}
    </Card>
  );
}
